import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} StudentType
 * @property {string} name - Name of student
 * @property {string} parent_email - Email of student
 * @property {string} [username] - Username of student is optional and if provided it must be unique
 * @property {string} [password] - Password of student is optional
 * @property {number} age - Age of student
 * @property {string|null} [assigned_to] - ID of assigned teacher (optional)
 */

/**
 * @description Custom hook to create new students in a school
 */
export const useCreateStudent = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [student, setStudent] = useState(null);
  const { axiosInstance } = useAxios();

  /**
   * @description Create a new student
   * @async
   * @param {StudentType} studentData
   * @param {()=> void} onSuccess
   * @returns {Promise<void>} Resolves once the API request completes
   */
  const createStudent = async (studentData, onSuccess) => {
    try {
      setLoading(true);
      setError("");
      const response = await axiosInstance.post(
        `${TENANT_DOMAIN}/student/store`,
        {
          name: studentData.name,
          parent_email: studentData.parent_email,
          username: studentData.username,
          password: studentData.password,
          age: studentData.age,
          assigned_to: studentData.assigned_to || null,
        },
      );
      const createdStudent = response.data.data;
      setStudent(createdStudent);
      setSuccess(true);
      if (onSuccess) onSuccess(createdStudent);
    } catch (err) {
      if (err.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        const apiErrors = err?.response?.data?.errors;

        let firstError = apiErrors && Object.values(apiErrors)?.[0]?.[0];

        if (firstError) {
          firstError = firstError.replace(/username/gi, "Student ID");
        }

        setError(
          firstError || err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN,
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return { loading, error, success, student, createStudent };
};
